// Versioned schema migrations. The DDL is generated from the domain descriptors so the
// SQLite schema (Node tests, Tauri/rusqlite) and the IndexedDB stores can never drift
// from the TypeScript model. `tools/gen-sql.ts` writes the same DDL to
// packages/store/sql/*.sql for the Rust side.
import { TABLES, TABLE_NAMES, SCHEMA_VERSION, type Dataset, type FieldDef, type TableName } from '@vero/domain';

export interface Migration {
  version: number;
  name: string;
  /** SQLite statements */
  sql: string[];
  /** IndexedDB object stores to create (table names) */
  idbStores: string[];
  /** optional in-memory data transform (applied to backups/imports from older schema versions) */
  transform?: (ds: Dataset) => Dataset;
}

export function sqlType(f: FieldDef): string {
  switch (f.t) {
    case 'number': return 'REAL';
    case 'int': case 'bool': return 'INTEGER';
    default: return 'TEXT';
  }
}

/**
 * Columns added to an existing table by a later migration (ALTER TABLE … ADD COLUMN).
 * `createTableSql(table, version)` leaves them out so the DDL of earlier migrations never changes
 * (the Rust shell and existing databases replay 0001–0003 exactly as they shipped).
 */
export const ADDED_COLUMNS: Record<number, Partial<Record<TableName, string[]>>> = {
  4: {
    persons: ['ownerId'],
    policies: ['category', 'parties', 'benefits', 'tags', 'color', 'instalment', 'replacesPolicyId'],
    policy_adjustments: ['previousAnnual', 'newAnnual', 'reason', 'source', 'reversesId', 'actor'],
    tasks: ['originalDueDate', 'rescheduleCount'],
    documents: ['category', 'fileId', 'filename', 'mime', 'size', 'sha256', 'docVersion', 'versionOf', 'source', 'status', 'restricted', 'claimId', 'quotationId', 'meetingId', 'expiryDate'],
    consents: ['lawfulBasis', 'scope', 'source', 'withdrawnAt'],
  },
  5: {
    cases: ['advisorId', 'category', 'reopenedAt'],
    case_requirements: ['status', 'waitingOn', 'requestedAt', 'receivedAt', 'expiresAt', 'documentId', 'templateId', 'mandatory'],
    reviews: ['answers', 'portfolioAnnual'],
    referrals: ['relationship', 'attempts', 'appointmentAt', 'nextActionDate'],
    campaigns: ['objective'],
  },
  7: {
    persons: ['clientSince'],
  },
  8: {
    renewals: ['stage', 'stageAt'],
  },
  12: {
    persons: ['doNotCall'],
    prospect_lists: ['scripts', 'lawfulBasis', 'consentSource'],
    prospects: ['doNotCall', 'lawfulBasis', 'consentSource', 'consentDate', 'scriptVariant', 'attempts'],
    sale_files: ['riskProfileId'],
  },
  13: {
    policies: ['deletedAt', 'deletedBy', 'deleteReason'],
    sync_ops: ['stepUpGrant'],
  },
  14: {
    policies: ['catalogInsurerId', 'catalogCategoryId', 'catalogPlanId'],
  },
  17: {
    plan_blocks: ['activity', 'personId'],
  },
  18: {
    documents: ['taxYearId'],
  },
};
function laterColumns(table: TableName, version: number): Set<string> {
  const out = new Set<string>();
  for (const [v, tables] of Object.entries(ADDED_COLUMNS)) if (Number(v) > version) for (const c of tables[table] ?? []) out.add(c);
  return out;
}
function columnSql(name: string, def: FieldDef): string {
  if (name === 'id') return `"id" TEXT PRIMARY KEY NOT NULL`;
  const nn = def.req ? ' NOT NULL' : '';
  const check = def.t === 'enum' && def.values ? ` CHECK ("${name}" IS NULL OR "${name}" IN (${def.values.map((v) => `'${v}'`).join(',')}))` : def.t === 'json' ? ` CHECK ("${name}" IS NULL OR json_valid("${name}"))` : '';
  return `"${name}" ${sqlType(def)}${nn}${check}`;
}

/** CREATE TABLE as of schema `version` (default: latest). */
export function createTableSql(table: TableName, version: number = SCHEMA_VERSION): string[] {
  const skip = laterColumns(table, version);
  const fields = Object.entries(TABLES[table] as Record<string, FieldDef>).filter(([name]) => !skip.has(name));
  const cols = fields.map(([name, def]) => columnSql(name, def));
  const stmts = [`CREATE TABLE IF NOT EXISTS "${table}" (\n  ${cols.join(',\n  ')}\n) STRICT`];
  for (const [name, def] of fields) {
    if (def.index) stmts.push(`CREATE INDEX IF NOT EXISTS "ix_${table}_${name}" ON "${table}" ("${name}")`);
  }
  return stmts;
}

/** ALTER TABLE statements for the columns a migration adds (all optional, so existing rows stay valid). */
export function addColumnsSql(version: number): string[] {
  const out: string[] = [];
  for (const [table, cols] of Object.entries(ADDED_COLUMNS[version] ?? {}) as [TableName, string[]][]) {
    const fields = TABLES[table] as Record<string, FieldDef>;
    for (const c of cols) {
      const def = fields[c];
      if (!def) throw new Error(`ADDED_COLUMNS ${version}: ${table}.${c} is not in the schema`);
      if (def.req) throw new Error(`ADDED_COLUMNS ${version}: ${table}.${c} must be optional`);
      out.push(`ALTER TABLE "${table}" ADD COLUMN ${columnSql(c, def)}`);
      if (def.index) out.push(`CREATE INDEX IF NOT EXISTS "ix_${table}_${c}" ON "${table}" ("${c}")`);
    }
  }
  return out;
}

/** Tables created by schema v1 — frozen list; later tables arrive through their own migration. */
const V1_TABLES: TableName[] = ['persons', 'households', 'household_members', 'policies', 'policy_adjustments', 'cases', 'case_requirements', 'case_events', 'tasks', 'notes', 'reviews', 'timeline', 'prospect_lists', 'prospects', 'documents', 'service_cases', 'consents', 'legacy_records', 'settings', 'audit_events'];

const V3_TABLES: TableName[] = ['needs_analyses', 'quotations', 'opportunities', 'referrals', 'communications', 'meetings', 'claims', 'templates'];

const V5_TABLES: TableName[] = ['branding_profiles', 'checklist_templates'];
const V6_TABLES: TableName[] = ['group_plans', 'group_members'];
const V8_TABLES: TableName[] = ['insured_items'];
const V9_TABLES: TableName[] = ['premium_arrears'];
/** schema 10 (ideas track, Oct 2026): one migration for every table the ideas added */
const V10_TABLES: TableName[] = ['nps_surveys', 'kyc_files'];
/** schema 11 (onboarding, help & compliance, Oct 2026): the IDD sale file */
const V11_TABLES: TableName[] = ['sale_files'];
/** schema 12 (prospecting calls, risk profile & suitability, family map, client summaries, Oct 2026) */
const V12_TABLES: TableName[] = ['call_logs', 'risk_profiles', 'family_links', 'client_summaries'];
/** schema 14 (handoff v08 #14): the advisor's insurer → category → plan catalogue */
const V14_TABLES: TableName[] = ['catalog_insurers', 'catalog_categories', 'catalog_plans'];
/** schema 15 (handoff v08 #25, Oct 2026): the advisor's end-of-day reports */
const V15_TABLES: TableName[] = ['daily_reports'];
/** schema 16 (AI business coach: daily goals + hour-by-hour planner, handoff v08 #22/#23, Oct 2026) */
const V16_TABLES: TableName[] = ['daily_goals', 'plan_blocks'];
/** v18 «Ο φορολογικός μου φάκελος»: the advisor's own tax years, documents/receipts and expected-documents checklist */
const V18_TABLES: TableName[] = ['tax_years', 'tax_items', 'tax_expected'];
const V4_TABLES: TableName[] = ['production_events', 'renewals', 'commission_rules', 'commission_entries', 'tax_rulesets', 'tax_snapshots', 'financial_items', 'life_events', 'campaigns', 'campaign_activities', 'automation_rules', 'automation_runs', 'extraction_drafts', 'custom_fields', 'sync_ops', 'sync_conflicts'];

export const MIGRATIONS: Migration[] = [
  {
    version: 1,
    name: 'init — canonical VÉRO OS schema v1',
    sql: [
      `CREATE TABLE IF NOT EXISTS "_meta" ("key" TEXT PRIMARY KEY NOT NULL, "value" TEXT) STRICT`,
      `CREATE TABLE IF NOT EXISTS "_snapshots" ("id" TEXT PRIMARY KEY NOT NULL, "meta" TEXT NOT NULL, "content" TEXT NOT NULL) STRICT`,
      ...V1_TABLES.flatMap((t) => createTableSql(t, 1)),
    ],
    idbStores: [...V1_TABLES],
  },
  {
    version: 2,
    name: 'Policy Comparator — comparisons table',
    sql: createTableSql('comparisons', 2),
    idbStores: ['comparisons'],
    transform: (ds) => ({ ...ds, comparisons: ds.comparisons ?? [] }),
  },
  {
    version: 3,
    name: 'Advisory depth — advisory tables: needs analyses, quotations, opportunities, referrals, communications, meetings, claims, templates',
    sql: V3_TABLES.flatMap((t) => createTableSql(t, 3)),
    idbStores: [...V3_TABLES],
    transform: (ds) => { const out = { ...ds } as Record<string, unknown[]>; for (const t of V3_TABLES) out[t] = out[t] ?? []; return out as unknown as Dataset; },
  },
  {
    version: 4,
    name: 'Business engine — business tables: production ledger, renewals, commission, tax center, financial items, life events, campaigns, automations, extraction, custom fields, sync; policy/document/adjustment columns',
    sql: [...addColumnsSql(4), ...V4_TABLES.flatMap((t) => createTableSql(t, 4)), `CREATE TABLE IF NOT EXISTS "_files" ("id" TEXT PRIMARY KEY NOT NULL, "meta" TEXT NOT NULL, "content" TEXT NOT NULL) STRICT`],
    idbStores: [...V4_TABLES],
    transform: (ds) => { const out = { ...ds } as Record<string, unknown[]>; for (const t of V4_TABLES) out[t] = out[t] ?? []; return out as unknown as Dataset; },
  },
  {
    version: 5,
    name: 'Platform & branding — branding profiles (advisor and agency), document checklist templates; requirement lifecycle, case assignment, review answers, referral attempts, campaign objective',
    sql: [...addColumnsSql(5), ...V5_TABLES.flatMap((t) => createTableSql(t, 5))],
    idbStores: [...V5_TABLES],
    transform: (ds) => { const out = { ...ds } as Record<string, unknown[]>; for (const t of V5_TABLES) out[t] = out[t] ?? []; return out as unknown as Dataset; },
  },
  {
    version: 6,
    name: 'Group plans — groups of employer schemes (group life, medical, pension, accident) and their member census',
    sql: V6_TABLES.flatMap((t) => createTableSql(t, 6)),
    idbStores: [...V6_TABLES],
    transform: (ds) => { const out = { ...ds } as Record<string, unknown[]>; for (const t of V6_TABLES) out[t] = out[t] ?? []; return out as unknown as Dataset; },
  },
  {
    version: 7,
    name: 'Client register — clientsince column on persons (the day each person became a client; optional, existing rows unchanged)',
    sql: addColumnsSql(7),
    idbStores: [],
  },
  {
    version: 8,
    name: 'Insured items — insured_items table: insured objects (vehicles, properties, businesses) per person; general-insurance renewal stage on renewals (optional columns, existing rows unchanged)',
    sql: [...addColumnsSql(8), ...V8_TABLES.flatMap((t) => createTableSql(t, 8))],
    idbStores: [...V8_TABLES],
    transform: (ds) => { const out = { ...ds } as Record<string, unknown[]>; for (const t of V8_TABLES) out[t] = out[t] ?? []; return out as unknown as Dataset; },
  },
  {
    version: 9,
    name: 'Premium arrears — premium_arrears table: overdue or failed premium collections per policy (entered by the advisor or imported from an insurer list); existing rows unchanged',
    sql: [...addColumnsSql(9), ...V9_TABLES.flatMap((t) => createTableSql(t, 9))],
    idbStores: [...V9_TABLES],
    transform: (ds) => { const out = { ...ds } as Record<string, unknown[]>; for (const t of V9_TABLES) out[t] = out[t] ?? []; return out as unknown as Dataset; },
  },
  {
    version: 10,
    name: 'Ideas — ideas track tables: client feedback (nps_surveys), KYC/AML file (kyc_files); existing rows unchanged',
    sql: [...addColumnsSql(10), ...V10_TABLES.flatMap((t) => createTableSql(t, 10))],
    idbStores: [...V10_TABLES],
    transform: (ds) => { const out = { ...ds } as Record<string, unknown[]>; for (const t of V10_TABLES) out[t] = out[t] ?? []; return out as unknown as Dataset; },
  },
  {
    version: 11,
    name: 'Salefiles — sale_files table: one IDD sale file per sale (demands and needs, recommendation, disclosures given); existing rows unchanged',
    sql: [...addColumnsSql(11), ...V11_TABLES.flatMap((t) => createTableSql(t, 11))],
    idbStores: [...V11_TABLES],
    transform: (ds) => { const out = { ...ds } as Record<string, unknown[]>; for (const t of V11_TABLES) out[t] = out[t] ?? []; return out as unknown as Dataset; },
  },
  {
    version: 12,
    name: 'Calls — call_logs (one row per dial), risk_profiles, family_links, client_summaries; optional columns for do-not-call, lawful basis, scripts and the sale file\'s risk profile; existing rows unchanged',
    sql: [...addColumnsSql(12), ...V12_TABLES.flatMap((t) => createTableSql(t, 12))],
    idbStores: [...V12_TABLES],
    transform: (ds) => { const out = { ...ds } as Record<string, unknown[]>; for (const t of V12_TABLES) out[t] = out[t] ?? []; return out as unknown as Dataset; },
  },
  {
    version: 13,
    name: 'Policy deletion — policy_deletion columns: soft deletion of a wrongly entered policy (deletedAt, deletedBy, deleteReason; restore within the window, never a hard delete) and the step-up grant of a queued sync operation; optional columns, existing rows unchanged',
    sql: addColumnsSql(13),
    idbStores: [],
  },
  {
    version: 14,
    name: 'Catalogue — catalog tables: the advisor\'s insurers, product categories and specific plans (catalog_insurers, catalog_categories, catalog_plans) and optional catalogue links on policies (catalogInsurerId, catalogCategoryId, catalogPlanId); the policy text fields stay; existing rows unchanged',
    sql: [...addColumnsSql(14), ...V14_TABLES.flatMap((t) => createTableSql(t, 14))],
    idbStores: [...V14_TABLES],
    transform: (ds) => { const out = { ...ds } as Record<string, unknown[]>; for (const t of V14_TABLES) out[t] = out[t] ?? []; return out as unknown as Dataset; },
  },
  {
    version: 15,
    name: 'Daily reports — daily_reports table: one end-of-day report per day (recorded CRM activity snapshot, the advisor\'s own entries, goals, self-rating 1–5, notes, feedback kept); existing rows unchanged',
    sql: [...addColumnsSql(15), ...V15_TABLES.flatMap((t) => createTableSql(t, 15))],
    idbStores: [...V15_TABLES],
    transform: (ds) => { const out = { ...ds } as Record<string, unknown[]>; for (const t of V15_TABLES) out[t] = out[t] ?? []; return out as unknown as Dataset; },
  },
  {
    version: 16,
    name: 'Daily goals — daily_goals (one snapshot per working day: the adaptive goals, why, adjustments) and plan_blocks (the advisor\'s own blocks in the hour-by-hour planner); existing rows unchanged',
    sql: [...addColumnsSql(16), ...V16_TABLES.flatMap((t) => createTableSql(t, 16))],
    idbStores: [...V16_TABLES],
    transform: (ds) => { const out = { ...ds } as Record<string, unknown[]>; for (const t of V16_TABLES) out[t] = out[t] ?? []; return out as unknown as Dataset; },
  },
  {
    version: 17,
    name: 'Plan activities — plan_activities columns: the type of each planner block (coffee / relationship meeting, referral meeting, calls, follow-up, admin, preparation, learning, team, travel, break, personal, other) and the optional person of a relationship meeting (plan_blocks.activity, plan_blocks.personId); optional columns, existing rows unchanged',
    sql: addColumnsSql(17),
    idbStores: [],
  },
  {
    version: 18,
    name: 'Tax folder — taxfolder tables: the advisor\'s own tax folder per year (tax_years), its documents and receipts (tax_items) and the expected-documents checklist (tax_expected); optional documents.taxYearId for the folder\'s files; existing rows unchanged',
    sql: [...addColumnsSql(18), ...V18_TABLES.flatMap((t) => createTableSql(t, 18))],
    idbStores: [...V18_TABLES],
    transform: (ds) => { const out = { ...ds } as Record<string, unknown[]>; for (const t of V18_TABLES) out[t] = out[t] ?? []; return out as unknown as Dataset; },
  },
];

export const LATEST_VERSION = MIGRATIONS[MIGRATIONS.length - 1]!.version;
if (LATEST_VERSION !== SCHEMA_VERSION) throw new Error(`Migrations (${LATEST_VERSION}) and domain SCHEMA_VERSION (${SCHEMA_VERSION}) disagree`);

/** Bring an in-memory dataset (e.g. from an older backup) up to the current schema. */
export function migrateDataset(ds: Dataset, fromVersion: number): Dataset {
  if (fromVersion > LATEST_VERSION) throw new Error(`Backup schema ${fromVersion} is newer than this app (${LATEST_VERSION}). Update VÉRO first.`);
  let out = ds;
  for (const m of MIGRATIONS) if (m.version > fromVersion && m.transform) out = m.transform(out);
  for (const t of TABLE_NAMES) if (!Array.isArray((out as Record<string, unknown>)[t])) (out as Record<string, unknown[]>)[t] = [];
  return out;
}

export function migrationDdl(version: number): string {
  const m = MIGRATIONS.find((x) => x.version === version)!;
  return `-- migration ${m.version}: ${m.name}\n${m.sql.map((s) => s + ';').join('\n')}`;
}
export function fullDdl(): string {
  return MIGRATIONS.map((m) => `-- migration ${m.version}: ${m.name}\n${m.sql.map((s) => s + ';').join('\n')}`).join('\n\n');
}
